<script setup>
import { computed, ref } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import FrontLayout from '@/Layouts/FrontLayout.vue';

const props = defineProps({
    setting: Object,
    kecamatan: Array,
    // SEO Props
    title: String,
    description: String,
    keywords: String,
    canonical_url: String,
    og_title: String,
    og_description: String,
    og_image: String,
    og_url: String,
    og_type: String,
    twitter_title: String,
    twitter_description: String,
    twitter_image: String,
});

// Helper for SEO
const pageTitle = computed(() => props.title || 'Area Layanan - Gotransport');
const pageDesc = computed(() => props.description || 'Daftar lengkap area layanan rental mobil Gotransport di seluruh kecamatan Banyuwangi.');

// WA Logic
const wa = computed(() => props.setting?.setting_no_wa || '6281234567890');
const waUrl = computed(() => `https://wa.me/${wa.value}?text=Halo,%20saya%20ingin%20info%20lebih%20lanjut`);
const telpUrl = computed(() => `tel:+62${(props.setting?.setting_no_telp || '').replace(/^0/, '')}`);

// Formatting Helper
const stripHtml = (html) => {
   let tmp = document.createElement("DIV");
   tmp.innerHTML = html;
   return tmp.textContent || tmp.innerText || "";
};
const truncate = (str, length) => str.length > length ? str.substring(0, length) + "..." : str;

const totalArticles = computed(() => {
    return props.kecamatan.reduce((acc, curr) => acc + (curr.artikel_published_count || (curr.artikel_published ? curr.artikel_published.length : 0)), 0);
});

// JSON-LD
const jsonLd = computed(() => ({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Area Layanan Rental Mobil Banyuwangi",
    "description": "Daftar lengkap area layanan rental mobil Gotransport di seluruh kecamatan Banyuwangi.",
    "url": window.location.href,
    "numberOfItems": props.kecamatan.length,
    "itemListElement": props.kecamatan.map((item, index) => ({
        "@type": "Place",
        "position": index + 1,
        "name": item.nama_kecamatan,
        "url": `${window.location.origin}/area-layanan/${item.slug}`
    }))
}));
</script>

<template>
    <FrontLayout 
        :setting="setting" 
        :title="pageTitle" 
        :metaDescription="pageDesc"
        :og_title="props.og_title || pageTitle"
        :og_description="props.og_description || pageDesc"
        :og_image="props.og_image"
        :og_url="props.og_url"
        :og_type="props.og_type || 'website'"
        :twitter_title="props.twitter_title || pageTitle"
        :twitter_description="props.twitter_description || pageDesc"
        :twitter_image="props.twitter_image"
        :meta_keywords="props.keywords"
    >
        <Head>

            <meta name="description" :content="pageDesc" />
            <meta v-if="props.keywords" name="keywords" :content="props.keywords" />
            <link v-if="props.canonical_url" rel="canonical" :href="props.canonical_url" />
            
            <meta property="og:title" :content="props.og_title || pageTitle" />
            <meta property="og:description" :content="props.og_description || pageDesc" />
            <meta v-if="props.og_image" property="og:image" :content="props.og_image" />
            <meta v-if="props.og_url" property="og:url" :content="props.og_url" />
            <meta property="og:type" :content="props.og_type || 'website'" />
            <meta property="og:site_name" content="Gotransport Banyuwangi" />
            
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" :content="props.twitter_title || pageTitle" />
            <meta name="twitter:description" :content="props.twitter_description || pageDesc" />
            <meta v-if="props.twitter_image" name="twitter:image" :content="props.twitter_image" />
            
            <!-- Inject JSON-LD -->
            <component is="script" type="application/ld+json">{{ JSON.stringify(jsonLd) }}</component>
        </Head>

        <!-- Hero Section (Orange Gradient) -->
        <section class="relative overflow-hidden">
             <div class="absolute inset-0 bg-gradient-to-br from-orange-600 via-orange-500 to-amber-400"></div>
             <div class="absolute inset-0 overflow-hidden pointer-events-none">
                <svg class="absolute right-0 top-0 text-white/10 w-64 h-64 -mr-16 -mt-16" fill="currentColor" viewBox="0 0 100 100"><circle cx="50" cy="50" r="50"/></svg>
                <div class="absolute left-10 bottom-10 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
             </div>

             <div class="container mx-auto px-4 lg:px-6 xl:px-8 relative z-10 text-center py-12 md:py-16">
                 <div class="flex justify-center mb-6">
                     <div class="inline-flex items-center gap-2 text-xs md:text-sm text-white/90 bg-white/10 backdrop-blur-md px-4 py-1.5 rounded-full border border-white/20 shadow-sm">
                        <Link href="/" class="hover:text-white transition-colors font-medium">Beranda</Link>
                        <span class="text-white/60">/</span>
                        <span class="text-white font-bold">Area Layanan</span>
                     </div>
                 </div>

                 <h1 class="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 tracking-tight drop-shadow-sm">Area Layanan Kami</h1>
                 <p class="text-amber-100 text-lg max-w-2xl mx-auto font-medium">
                     Gotransport melayani rental mobil di seluruh kecamatan Banyuwangi dengan armada terlengkap dan pelayanan terpercaya untuk kebutuhan transportasi Anda
                 </p>
             </div>
             
             <div class="absolute bottom-0 left-0 right-0 text-gray-50">
                 <svg viewBox="0 0 1440 48" fill="currentColor" class="w-full h-auto block">
                     <path d="M0,48 L1440,48 L1440,0 C1440,0 1140,48 720,48 C300,48 0,0 0,0 L0,48 Z"></path>
                 </svg>
             </div>
        </section>

        <!-- Stats Overview -->
        <section class="py-12 bg-gray-50">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <div class="text-center mb-10">
                    <h2 class="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Jangkauan Layanan Kami</h2>
                    <p class="text-gray-600 max-w-2xl mx-auto">
                        {{ setting?.setting_title || 'Gotransport' }} telah dipercaya melayani berbagai wilayah di Banyuwangi dengan komitmen memberikan pelayanan rental mobil terbaik
                    </p>
                </div>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                    <div class="bg-white border border-gray-100 rounded-2xl p-4 hover:border-blue-500 hover:shadow-lg transition-all duration-300 group">
                        <div class="flex items-center gap-6">
                            <div class="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center text-blue-600 text-2xl group-hover:scale-110 transition-transform">
                                <i class="fa fa-map-marker"></i>
                            </div>
                            <div>
                                <div class="text-4xl font-extrabold text-gray-900 mb-1">{{ kecamatan.length }}</div>
                                <div class="text-sm font-bold text-gray-600 uppercase tracking-wide">Kecamatan Terlayani</div>
                                <p class="text-xs text-gray-500 mt-2">Wilayah yang telah mempercayai layanan rental mobil kami</p>
                            </div>
                        </div>
                    </div>
                    
                    <div class="bg-white border border-gray-100 rounded-2xl p-4 hover:border-green-500 hover:shadow-lg transition-all duration-300 group">
                        <div class="flex items-center gap-6">
                            <div class="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center text-green-600 text-2xl group-hover:scale-110 transition-transform">
                                <i class="fa fa-file-text"></i>
                            </div>
                            <div>
                                <div class="text-4xl font-extrabold text-gray-900 mb-1">{{ totalArticles }}</div>
                                <div class="text-sm font-bold text-gray-600 uppercase tracking-wide">Artikel Informatif</div>
                                <p class="text-xs text-gray-500 mt-2">Panduan dan informasi lengkap tentang rental mobil</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Kecamatan Grid -->
                <div class="text-center mb-8">
                    <span class="inline-block px-3 py-1 bg-orange-100 text-orange-700 text-xs font-bold uppercase rounded-full mb-3">
                        Jangkauan Luas
                    </span>
                    <h3 class="text-2xl md:text-3xl font-bold text-gray-900">Kecamatan yang Kami Layani</h3>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <article v-for="item in kecamatan" :key="item.id" class="bg-white border border-gray-200 rounded-2xl p-4 hover:border-orange-400 hover:shadow-xl transition-all duration-300 group flex flex-col h-full">
                        <div class="flex items-center gap-4 mb-4">
                            <div class="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center text-orange-600 group-hover:bg-orange-600 group-hover:text-white transition-colors duration-300">
                                <i class="fa fa-map-marker text-xl"></i>
                            </div>
                            <h3 class="text-xl font-bold text-gray-900 group-hover:text-orange-600 transition-colors">
                                {{ item.nama_kecamatan }}
                            </h3>
                        </div>

                        <div class="mb-6 flex-grow">
                             <p v-if="item.deskripsi" class="text-sm text-gray-600 line-clamp-3" v-html="stripHtml(item.deskripsi).substring(0, 120) + '...'"></p>
                             <p v-else class="text-sm text-gray-400 italic">Tidak ada deskripsi tersedia.</p>
                        </div>

                        <div class="border-t border-gray-100 pt-4 mt-auto">
                            <!-- Stats Small -->
                            <div class="flex items-center justify-between mb-4 text-xs text-gray-500">
                                <div class="flex items-center gap-1.5">
                                     <i class="fa fa-file-text text-gray-400"></i>
                                     <span class="font-bold text-gray-900">{{ item.artikel_published?.length || 0 }}</span> Artikel
                                </div>
                            </div>

                            <!-- Buttons -->
                            <div class="flex gap-3">
                                <Link :href="`/area-layanan/${item.slug}`" 
                                   class="flex-1 text-center px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-xl transition-colors shadow-blue-200 hover:shadow-lg">
                                    <i class="fa fa-eye mr-1"></i> Detail
                                </Link>
                                <a href="/contact"
                                   class="flex-1 text-center px-4 py-2.5 border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white text-sm font-bold rounded-xl transition-colors">
                                    <i class="fa fa-phone mr-1"></i> Hubungi
                                </a>
                            </div>
                        </div>
                    </article>
                    
                    <!-- Empty State -->
                    <div v-if="kecamatan.length === 0" class="col-span-full text-center py-12">
                        <div class="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full text-gray-400 text-3xl mb-4">
                            <i class="fa fa-map-marker"></i>
                        </div>
                        <h3 class="text-xl font-bold text-gray-900 mb-2">Belum Ada Area Layanan</h3>
                        <p class="text-gray-600 mb-4">Area layanan sedang dalam proses pengembangan. Hubungi kami untuk informasi lebih lanjut.</p>
                        <a href="/contact" class="inline-block px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-colors">
                            <i class="fa fa-phone mr-2"></i> Hubungi Kami
                        </a>
                    </div>
                </div>
            </div>
        </section>

        <!-- Map Section - Full Width -->
        <section v-if="setting?.setting_map" class="py-12 bg-white border-t border-gray-200">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl mb-8">
                <div class="text-center">
                    <h3 class="text-2xl md:text-3xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-2">
                        <i class="fa fa-map-marker text-orange-600"></i>
                        Lokasi Kantor {{ setting?.setting_title || 'Gotransport' }}
                    </h3>
                    <p class="text-gray-600 max-w-2xl mx-auto">
                        Kunjungi kantor kami untuk konsultasi rental mobil di seluruh area kecamatan Banyuwangi.
                        Tim berpengalaman siap membantu kebutuhan transportasi Anda.
                    </p>
                </div>
            </div>

            <div class="w-full h-[450px] relative overflow-hidden bg-gray-100">
                <div class="w-full h-full" v-html="setting.setting_map"></div>
            </div>
        </section>
    </FrontLayout>
</template>

<style scoped>
/* Ensure map iframe takes full space */
:deep(iframe) {
    width: 100% !important;
    height: 100% !important;
    border: none;
}
</style>
